import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { SiStripe } from "react-icons/si";
import { useApi, errText } from "@/lib/api";
import { openOAuthPopup, finishOAuthPopup } from "@/lib/redirect";
import { IntegrationCard as Card, OwnerActions, statusLabel, showOAuthResult } from "@/components/integrations/IntegrationCard";
import { HealthNote, useHealth } from "@/components/integrations/Health";
import { SetupChecklist } from "@/components/integrations/SetupChecklist";
import { BankCard } from "@/components/integrations/BankCard";
import { QboCard } from "@/components/integrations/QboCard";
import { PipelineCard } from "@/components/integrations/PipelineCard";

const REDIRECT = () => `${window.location.origin}/app/integrations`;

const StripeCard = () => {
  const api = useApi();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const handled = useRef(false);
  const { data } = useQuery({ queryKey: ["connect"], queryFn: () => api.get("/stripe-connect/status"), refetchOnWindowFocus: "always" });

  useEffect(() => {
    const code = params.get("code"), state = params.get("state");
    if (params.get("realmId")) return;
    if (params.get("error") && !String(state || "").startsWith("qb_")) {
      const message = params.get("error") === "access_denied" ? "Stripe connection cancelled" : (params.get("error_description") || "Stripe connection failed");
      if (!finishOAuthPopup({ ok: false, message })) toast.error(message, { id: "stripe-oauth-error" });
      setParams({});
      return;
    }
    if (!code || handled.current) return;
    handled.current = true;
    api.post("/stripe-connect/callback", { code, state })
      .then(() => ({ ok: true, message: "Stripe connected — revenue is now live" }))
      .catch((e) => ({ ok: false, message: errText(e, "Stripe connection failed") }))
      .then((r) => { if (!finishOAuthPopup(r)) showOAuthResult(r, qc); })
      .finally(() => setParams({}));
  }, [params, api, qc, setParams]);

  const connect = async () => {
    try {
      await openOAuthPopup(async () => (await api.post("/stripe-connect/authorize", { redirect_uri: REDIRECT() })).url,
        { onDone: (r) => showOAuthResult(r, qc), onClosed: () => qc.invalidateQueries({ queryKey: ["connect"] }) });
    } catch (e) { toast.error(errText(e, "Couldn't start Stripe Connect")); }
  };
  const disconnect = async () => { await api.post("/stripe-connect/disconnect"); qc.invalidateQueries(); toast("Stripe disconnected"); };
  const h = useHealth()?.providers?.stripe;
  const broken = data?.connected && h?.status === "needs_reconnect";

  return (
    <Card testid="integration-stripe" icon={SiStripe} color="#635BFF" name="Stripe" status={statusLabel(data?.connected, h)}
      desc={data?.connected ? `Reading MRR and customers from account ${data.account_id}. CalibRunway only reads your revenue data and never creates charges or changes settings.` : "Connect your Stripe account to pull real MRR and customer counts. Stripe's connection screen asks for full access (it no longer offers read-only), but CalibRunway only reads revenue data and never creates charges or changes settings."}>
      {data?.connected && <HealthNote health={h} />}
      <OwnerActions testid="integration-stripe">
      {data?.connected && !broken ? (
        <button data-testid="stripe-disconnect-btn" onClick={disconnect} className="w-full rounded-full border border-[#2A2A2A] py-2.5 text-sm hover:border-[#FF5C7A] hover:text-[#FF5C7A] transition-colors">Disconnect</button>
      ) : (
        <>
          <button data-testid="stripe-connect-btn" onClick={connect} disabled={data && !data.configured}
            className="w-full rounded-full bg-[#635BFF] py-2.5 text-sm font-semibold hover:shadow-[0_0_20px_rgba(99,91,255,0.6)] transition-shadow disabled:bg-[#2A2A2A] disabled:text-[#8A8A8A] disabled:cursor-not-allowed">{broken ? "Reconnect Stripe" : "Connect with Stripe"}</button>
          {data && !data.configured && <p data-testid="stripe-connect-pending" className="mt-2 text-[11px] text-[#A3A3A3]">Stripe Connect client ID not configured yet — using demo revenue.</p>}
        </>
      )}
      </OwnerActions>
    </Card>
  );
};

export default function Integrations() {
  const api = useApi();
  const { data } = useQuery({ queryKey: ["dashboard"], queryFn: () => api.get("/dashboard") });
  const { data: expenses } = useQuery({ queryKey: ["expenses"], queryFn: () => api.get("/expenses") });
  return (
    <div data-testid="integrations-page">
      <p className="font-mono text-xs text-[#A3A3A3]">MULTI-SYNC SOURCES</p>
      <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">Integrations</h1>
      <SetupChecklist />
      <div className="mt-8 grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        <StripeCard />
        <BankCard accounts={data?.bank_accounts} />
        <QboCard vendorCount={expenses?.length} />
        <PipelineCard />
      </div>
    </div>
  );
}
